import { FormBuilder } from '@/components/formBuilder';
import { KText } from '@/ui-library/components';
import {
  Box,
  Button,
  Flex,
  Icon,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react';
import { Calendar } from '@kula-ai/icons/dual-tone/Calendar';
import { Edit05 } from '@kula-ai/icons/dual-tone/Edit05';
import { Trash01 } from '@kula-ai/icons/dual-tone/Trash01';
import { useRef, useState, useEffect, useMemo } from 'react';
import { CustomDateNavHeader } from './CustomDateNavHeader';
import moment from 'moment-timezone';
import { TIME_ZONE_CONSTANTS } from '@/utils/timeZoneConstants';
import { useDateFormat } from '@/hooks';

const ScheduleBody = ({
  formConfig,
  handleFormSubmit,
  onSchedule,
  isScheduling,
  scheduleRef,
  onClose,
  initialValues = {},
}) => {
  const schedule_at = scheduleRef?.current?.values?.schedule_at;
  const isFutureDate = schedule_at?.isFutureDate;

  const isFutureYear = useMemo(() => {
    if (!schedule_at?.formattedTime) return false;

    const dateYear = moment.parseZone(schedule_at.formattedTime).year();
    const currentYear = moment().year();

    return dateYear > currentYear;
  }, [schedule_at?.formattedTime]);

  const shouldBeEnabled = isFutureDate || isFutureYear;

  useEffect(() => {
    if (scheduleRef?.current?.values?.schedule_at?.formattedTime) {
      const formattedTime =
        scheduleRef.current.values.schedule_at.formattedTime;
      const year = parseInt(formattedTime.substring(0, 4), 10);

      if (year >= 2025) {
        scheduleRef.current.values.schedule_at.isFutureDate = true;
        scheduleRef.current.setFieldValue('schedule_at', {
          ...scheduleRef.current.values.schedule_at,
          isFutureDate: true,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleRef?.current?.values?.schedule_at?.formattedTime]);

  return (
    <Flex flexDir="column" p="12px 24px 24px 24px">
      <FormBuilder
        config={formConfig}
        onSubmit={handleFormSubmit}
        formRef={scheduleRef}
        initialValues={initialValues}
      />
      <Flex alignSelf="end" gap="12px">
        <Button
          onClick={onClose}
          variant="secondary"
          data-test-id="secondary-cta-button"
        >
          Cancel
        </Button>
        <Button
          onClick={async () => {
            if (scheduleRef?.current) {
              await scheduleRef?.current?.submitForm();
              const values = scheduleRef?.current?.values;
              if (scheduleRef?.current?.isValid && onSchedule) {
                await onSchedule(values);
                // Close popover after API call completes (success or failure)
                onClose();
              } else {
                onClose();
              }
            }
          }}
          isDisabled={!shouldBeEnabled}
          variant="primary"
          data-test-id="set-schedule-button"
          isLoading={isScheduling}
        >
          Schedule
        </Button>
      </Flex>
    </Flex>
  );
};

export const ScheduleForLater = ({
  scheduleRef,
  scheduleDetails,
  onSchedule,
  onCancelSchedule,
  isScheduling = false,
  isCanceling = false,
  isScheduled = false,
  styles = {},
}) => {
  const { isOpen, onClose, onToggle } = useDisclosure();

  const ref = useRef(null);
  interface ScheduleData {
    timezone: any;
    schedule_at?: string;
  }

  const timeZone =
    scheduleDetails?.scheduled_at_timezone || scheduleDetails?.timezone;
  const [scheduleData, setScheduleData] = useState<ScheduleData>({
    timezone: null,
  });

  const date = moment.parseZone(scheduleDetails?.scheduled_at);
  const { fullName: tzName, abbreviation: tzAbbr } =
    TIME_ZONE_CONSTANTS[timeZone] || {};
  const { timeFormat } = useDateFormat();

  const renderScheduleDetail = () => {
    const formValues = scheduleRef?.current?.values || {};
    const { schedule_at } = formValues;
    if (schedule_at?.formattedTime && schedule_at?.timezoneValue?.key) {
      const momentDate = moment.parseZone(schedule_at.formattedTime);
      const { fullName: newTzName, abbreviation: newTzAbbr } =
        TIME_ZONE_CONSTANTS[schedule_at.timezoneValue.key] || {};

      return (
        momentDate.format('dddd, MMMM D YYYY [at]') +
        ` ${momentDate.format(timeFormat)}` +
        ` ${newTzAbbr} (${newTzName})`
      );
    }

    if (scheduleDetails?.scheduled_at) {
      return (
        date.format('dddd, MMMM D YYYY [at]') +
        ` ${date.format(timeFormat)}` +
        ` ${tzAbbr} (${tzName})`
      );
    }

    return '';
  };

  const scheduled_date = renderScheduleDetail();

  // Get the current schedule values - prefer scheduleDetails (API source of truth)
  const getEditInitialValues = () => {
    // Use scheduleDetails from API as it's the authoritative source after successful schedule
    return {
      schedule_at: {
        formattedTime: scheduleDetails?.scheduled_at,
        timezoneValue: {
          key: timeZone,
          label: timeZone,
        },
        isFutureDate: true,
        time: moment.parseZone(scheduleDetails?.scheduled_at).format('HH:mm'),
      },
    };
  };

  useOutsideClick({
    ref: ref,
    handler: () => {
      onClose();
    },
  });

  const handleTimeZone = (values) => {
    setScheduleData(values);
  };

  const formConfig = [
    {
      name: 'schedule_at',
      type: 'dateWithTimeZone',
      enableTime: true,
      isInline: true,
      disablePreviousDate: true,
      returnWithTimeZoneFormat: true,
      className: 'schedule-datepicker',
      customHeader: CustomDateNavHeader,
      customOnChange: handleTimeZone,
      showError: false,
    },
  ];

  // This function now only performs form validation and updates local form state
  const handleFormSubmit = (values) => {
    const updatedScheduleData = { ...scheduleData, ...values };
    scheduleRef?.current?.setValues({
      ...values,
    });

    return updatedScheduleData;
  };

  const handleDeleteSchedule = async () => {
    if (onCancelSchedule) {
      await onCancelSchedule();
    }
  };

  if (scheduleDetails?.status === 'scheduled' || isScheduled) {
    return (
      <Box
        p="12px"
        m="8px 24px"
        borderRadius="sm"
        bg="primary-25"
        flexDir="column"
        gap="8px"
        {...styles}
      >
        <Flex alignItems="center" justifyContent="space-between">
          <Flex alignItems="center" gap="3px">
            <Icon as={Calendar}></Icon>
            <KText size="sm-medium">Scheduled for </KText>
          </Flex>
          <Flex alignItems="center" gap="12px">
            <Box ref={ref}>
              <Popover isOpen={isOpen} placement="top-end" gutter={12}>
                {/* @ts-ignore */}
                <PopoverTrigger>
                  <Box>
                    <Icon
                      as={Edit05}
                      cursor={isScheduling ? 'not-allowed' : 'pointer'}
                      onClick={isScheduling ? null : onToggle}
                      data-test-id="edit-schedule-button"
                      opacity={isScheduling ? 0.5 : 1}
                    ></Icon>
                  </Box>
                </PopoverTrigger>
                <PopoverContent p={0} w="330px">
                  <PopoverBody p={0} position="relative">
                    <ScheduleBody
                      key={`edit-${scheduleDetails?.scheduled_at}`}
                      formConfig={formConfig}
                      handleFormSubmit={handleFormSubmit}
                      onClose={onClose}
                      scheduleRef={scheduleRef}
                      initialValues={getEditInitialValues()}
                      onSchedule={onSchedule}
                      isScheduling={isScheduling}
                    />
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            </Box>
            <Box>
              <Icon
                as={Trash01}
                cursor={isCanceling ? 'not-allowed' : 'pointer'}
                onClick={handleDeleteSchedule}
                data-test-id="delete-schedule-button"
                opacity={isCanceling ? 0.5 : 1}
              ></Icon>
            </Box>
          </Flex>
        </Flex>
        <KText mt="6px">{scheduled_date}</KText>
      </Box>
    );
  }

  return (
    <Box ref={ref}>
      <Popover isOpen={isOpen} placement="top-end" gutter={12}>
        {/* @ts-ignore */}
        <PopoverTrigger>
          <Button
            variant="tertiary"
            onClick={() => {
              if (!isScheduling) {
                onToggle();
              }
            }}
            data-test-id="schedule-later-button"
            isDisabled={isScheduling}
            isLoading={isScheduling}
          >
            Schedule for later
          </Button>
        </PopoverTrigger>
        <PopoverContent p={0} w="330px">
          <PopoverBody p={0} position="relative">
            <ScheduleBody
              formConfig={formConfig}
              handleFormSubmit={handleFormSubmit}
              onClose={onClose}
              scheduleRef={scheduleRef}
              onSchedule={onSchedule}
              isScheduling={isScheduling}
            />
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};
